import { designTokens } from "@amplecare/design-tokens";
import { Text, View } from "react-native";

import { styles } from "./AppShell.styles.js";
import {
  getLegacyDetailPreviewPalette,
  getLegacySectionChromeColors,
  resolveLegacyCompactLeadText,
  resolveLegacyPreviewFallbackSecondaryText,
  type AppShellTheme,
  type LegacyDetailPreviewTone,
  type LegacySectionChromeTone,
} from "./shell-core.js";

export function renderLegacySectionSummaryRail(input: {
  items: ReadonlyArray<{
    id: string;
    title: string;
    supportText: string;
    leadText: string;
    countText: string;
    tone: LegacySectionChromeTone;
    isSelected?: boolean;
  }>;
  layout: typeof designTokens.semanticLayout;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const { items, layout, theme, tokens } = input;

  return (
    <View
      style={[
        styles.workspaceSectionSummaryGrid,
        {
          gap: tokens.spacing.sm,
        },
      ]}
    >
      {items.map((item) => {
        const chromeColors = getLegacySectionChromeColors(
          item.tone,
          item.isSelected ?? false,
          theme,
        );

        return (
          <View
            key={item.id}
            style={[
              styles.sectionChromeButton,
              styles.workspaceSectionSummaryCard,
              {
                backgroundColor: chromeColors.backgroundColor,
                borderColor: chromeColors.borderColor,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.md,
                padding: tokens.spacing.sm,
              },
            ]}
          >
            <View
              style={[
                styles.sectionChromeLead,
                {
                  backgroundColor: chromeColors.leadBackgroundColor,
                  borderColor: chromeColors.leadBorderColor,
                  borderRadius: layout.controlRadius,
                },
              ]}
            >
              <Text
                style={[
                  styles.sectionChromeLeadText,
                  {
                    color: chromeColors.leadTextColor,
                    fontFamily: layout.bodyFontFamily,
                  },
                ]}
              >
                {item.leadText}
              </Text>
            </View>

            <View
              style={[
                styles.sectionChromeCopy,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: chromeColors.titleColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {item.title}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  styles.sectionChromeSupportText,
                  {
                    color: chromeColors.supportColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    lineHeight: tokens.typography.lineHeight.sm,
                  },
                ]}
              >
                {item.supportText}
              </Text>
            </View>

            <View
              style={[
                styles.sectionChromeCountBadge,
                {
                  backgroundColor: chromeColors.countBackgroundColor,
                  borderColor: chromeColors.countBorderColor,
                  borderRadius: tokens.radius.pill,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.xs,
                },
              ]}
            >
              <Text
                style={[
                  styles.sectionChromeCountText,
                  {
                    color: chromeColors.countTextColor,
                    fontFamily: layout.bodyFontFamily,
                  },
                ]}
              >
                {item.countText}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

export function renderLegacyPreviewListRow(input: {
  id: string;
  title: string;
  subtitle?: string | null;
  supportingText?: string | null;
  badgeText?: string | null;
  leadPrimaryText?: string | null;
  leadSecondaryText?: string | null;
  tone: LegacyDetailPreviewTone;
  layout: typeof designTokens.semanticLayout;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    id,
    title,
    subtitle = null,
    supportingText = null,
    badgeText = null,
    leadPrimaryText = null,
    leadSecondaryText = null,
    tone,
    layout,
    theme,
    tokens,
  } = input;
  const palette = getLegacyDetailPreviewPalette(tone, theme);

  return (
    <View
      key={id}
      style={[
        styles.legacyDetailPreviewCard,
        {
          backgroundColor: palette.backgroundColor,
          borderColor: palette.borderColor,
          borderRadius: layout.controlRadius,
          gap: tokens.spacing.md,
          padding: tokens.spacing.sm,
        },
      ]}
    >
      <View
        style={[
          styles.legacyDetailPreviewLeadingColumn,
          {
            backgroundColor: palette.leadingBackgroundColor,
            borderColor: theme.borderSubtle,
            borderRadius: layout.controlRadius,
            paddingHorizontal: tokens.spacing.sm,
            paddingVertical: tokens.spacing.md,
          },
        ]}
      >
        <Text
          style={[
            styles.legacyDetailPreviewPrimaryText,
            {
              color: palette.leadingTextColor,
              fontFamily: layout.displayFontFamily,
              fontSize: tokens.typography.fontSize.md,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          {leadPrimaryText ?? resolveLegacyCompactLeadText(title)}
        </Text>
        <Text
          style={[
            styles.legacyDetailPreviewSecondaryText,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              lineHeight: tokens.typography.lineHeight.xs,
            },
          ]}
        >
          {leadSecondaryText ??
            resolveLegacyPreviewFallbackSecondaryText(title)}
        </Text>
      </View>

      <View
        style={[
          styles.legacyDetailPreviewContent,
          {
            gap: tokens.spacing.xs,
          },
        ]}
      >
        <View
          style={[
            styles.headerRow,
            {
              alignItems: "center",
              justifyContent: "space-between",
            },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
              },
            ]}
          >
            {title}
          </Text>
          {badgeText ? (
            <View
              style={[
                styles.inlinePill,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: tokens.radius.pill,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.xs,
                },
              ]}
            >
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: palette.leadingTextColor,
                    fontFamily: layout.bodyFontFamily,
                  },
                ]}
              >
                {badgeText}
              </Text>
            </View>
          ) : null}
        </View>

        {subtitle ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {subtitle}
          </Text>
        ) : null}

        {supportingText ? (
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            {supportingText}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
